import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { FieldValues, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { UseFormResetField } from 'react-hook-form/dist/types/form';
import { compareDesc } from 'date-fns';

import { prepareOptionKpiValues } from '@utils/kpi-utils/common';
import { endOfDayInMilliseconds, startOfDayInMilliseconds } from '@utils/kpi-utils/date';
import { getDateSelectionParameters } from '@components/Kpi/Parameters/utils';

import { IFlatMoTreeNode } from '@interfaces/components/tree';
import { IEditPresetDTO, IPresetDTO } from '@interfaces/kpi/dto/kpiDTO';
import { IUpdatePresetDTO } from '@interfaces/kpi/dto/kpiPresetsDTO';
import { IKPISite, ISelectedOnMapSitesByVendor, TResetSitesAndVendorArgs } from '@interfaces/kpi/kpiCommon';
import { IGroupSelectOptionsKpi } from '@components/common/GroupSelectKpi/GroupSelect.types';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { TTimeAggregationTypes, TVendorsValues } from '@enums/common';
import { EFormFields } from '@enums/kpi-api';
import { EGranularityValue } from '@enums/kpi-common';

import { dateOffsetOptions, granularitiesList } from '@components/Kpi/Parameters/constants';

interface UsePresetLogicProps {
	selectedPreset?: IPresetDTO;
	presetValue?: TSelectOptionType<string>;
	minDate: Date;
	optionsKpi: IGroupSelectOptionsKpi[];
	kpiGroupData: IFlatMoTreeNode[];
	countersGroupData: IFlatMoTreeNode[];
	treeKpi: IFlatMoTreeNode[];
	treeCounters: IFlatMoTreeNode[];
	vendor: TSelectOptionType<TVendorsValues>;
	setValue: UseFormSetValue<FieldValues>;
	resetField: UseFormResetField<FieldValues>;
	onChangeKpi: (values: Array<IGroupSelectOptionsKpi>, isUpdateDirty?: boolean) => void;
	resetKpi: () => void;
	handleUpdateTreeWithWorker: (tree: IFlatMoTreeNode[], kpiIds: string[]) => Promise<IFlatMoTreeNode[]>;
	setTreeKpi: Dispatch<SetStateAction<IFlatMoTreeNode[]>>;
	setTreeCounters: Dispatch<SetStateAction<IFlatMoTreeNode[]>>;
	setTreeKpiIds: (ids: string[]) => void;
	setTreeCountersIds: (ids: string[]) => void;
	setInitKpiData: (nodes: IFlatMoTreeNode[]) => void;
	setInitCountersData: (nodes: IFlatMoTreeNode[]) => void;
	setIsDirty: Dispatch<SetStateAction<boolean>>;
	openPresetForm: (presetDate: IPresetDTO) => void;
	updatePreset: (values: IUpdatePresetDTO) => void;
	getValues: UseFormGetValues<FieldValues>;
	isOranVendor: boolean;
	defaultSelectedSitesOnMap?: ISelectedOnMapSitesByVendor;
	resetSitesAndVendor: (...params: TResetSitesAndVendorArgs) => void;
	defaultFrom: number;
	defaultTo: number;
	setIsLoadingSetPreset?: Dispatch<SetStateAction<boolean>>;
	prepareDataForSubmit: (values: FieldValues) => {
		timeAggregationType: TTimeAggregationTypes;
		sites: IKPISite[];
	};
	getKpiIds: (options: Array<IGroupSelectOptionsKpi>) => string[] | null;
	dynamicDatesOptions: TSelectOptionType<string>[];
}

export const usePresetLogic = ({
	selectedPreset,
	presetValue,
	minDate,
	optionsKpi,
	kpiGroupData,
	countersGroupData,
	treeKpi,
	treeCounters,
	vendor,
	setValue,
	resetField,
	onChangeKpi,
	resetKpi,
	handleUpdateTreeWithWorker,
	setTreeKpi,
	setTreeCounters,
	setTreeKpiIds,
	setTreeCountersIds,
	setInitKpiData,
	setInitCountersData,
	setIsDirty,
	openPresetForm,
	updatePreset,
	getValues,
	isOranVendor,
	defaultSelectedSitesOnMap,
	resetSitesAndVendor,
	defaultFrom,
	defaultTo,
	setIsLoadingSetPreset,
	prepareDataForSubmit,
	getKpiIds,
	dynamicDatesOptions,
}: UsePresetLogicProps) => {
	const setDefaultDates = useCallback(() => {
		setValue(EFormFields.from, defaultFrom);
		setValue(EFormFields.to, defaultTo);
	}, [defaultFrom, defaultTo, setValue]);

	const availableKpiIds = useMemo(() => {
		return new Set(
			kpiGroupData.map(item => item.dbId)
				.filter(id => id),
		);
	}, [kpiGroupData]);

	const availableCountersIds = useMemo(() => {
		return new Set(
			countersGroupData.map(item => item.dbId)
				.filter(id => id),
		);

	}, [countersGroupData]);

	const setSelectedPreset = useCallback(async () => {
		if (!selectedPreset) {
			return;
		}

		if (selectedPreset?.vendor && !defaultSelectedSitesOnMap) {
			resetSitesAndVendor(selectedPreset, false);
		}

		if (selectedPreset.aggregationType) {
			setValue(EFormFields.aggregationType, selectedPreset.aggregationType);
		} else {
			resetField(EFormFields.aggregationType);
		}

		if (selectedPreset.from) {
			if (compareDesc(selectedPreset.from, minDate) > 0) {
				setDefaultDates();
			} else {
				setValue(EFormFields.from, selectedPreset.from);
			}
		} else {
			setValue(EFormFields.from, null);
		}

		if (selectedPreset.to) {
			if (compareDesc(selectedPreset.to, minDate) > 0) {
				setDefaultDates();
			} else {
				setValue(EFormFields.to, selectedPreset.to);
			}
		} else {
			setValue(EFormFields.to, null);
		}

		if (selectedPreset.dateSelectionMode) {
			setValue(EFormFields.dateSelectionMode, selectedPreset.dateSelectionMode);
		}

		if (selectedPreset.dynamicDateRange) {
			const preparedDynamicDateRange: TSelectOptionType<string> | undefined = dynamicDatesOptions.find(option => {
					return option?.value === selectedPreset.dynamicDateRange;
				},
			);

			if (preparedDynamicDateRange) {
				setValue(EFormFields.dynamicDateRange, preparedDynamicDateRange);
			}
		} else {
			resetField(EFormFields.dynamicDateRange);
		}

		if (selectedPreset.startDateOffset !== undefined) {
			const preparedStartDateOffset: TSelectOptionType<number> | undefined = dateOffsetOptions.find(option => {
					return option?.value === selectedPreset.startDateOffset;
				},
			);

			if (preparedStartDateOffset) {
				setValue(EFormFields.startDateOffset, preparedStartDateOffset);
			}
		} else {
			resetField(EFormFields.startDateOffset);
		}

		if (selectedPreset.isAutomaticGranularity) {
			setValue(EFormFields.isAutomaticGranularity, selectedPreset.isAutomaticGranularity);
			setValue(EFormFields.granularity, null);
		} else if (selectedPreset.granularity && selectedPreset.isAutomaticGranularity !== undefined) {
			const preparedGranularity: TSelectOptionType<EGranularityValue> | undefined = granularitiesList.find(
				granularity => granularity.value === selectedPreset.granularity,
			);

			if (preparedGranularity) {
				setValue(EFormFields.isAutomaticGranularity, false);
				setValue(EFormFields.granularity, preparedGranularity);
			}
		}

		if (selectedPreset.kpiIds) {
			if (isOranVendor) {
				setIsLoadingSetPreset?.(true);

				const idsForKpi = selectedPreset.kpiIds.filter((id) => availableKpiIds.has(id));
				const idsForCounters = selectedPreset.kpiIds.filter((id) => availableCountersIds.has(id));

				try {
					if (idsForKpi.length) {
						const updatedTreeKpi = await handleUpdateTreeWithWorker(treeKpi, idsForKpi);

						setTreeKpi(updatedTreeKpi);
						setInitKpiData(updatedTreeKpi);
						setTreeKpiIds(idsForKpi);
					} else {
						const updatedTreeKpi = await handleUpdateTreeWithWorker(treeKpi, idsForKpi);

						setTreeKpi(updatedTreeKpi);
						setInitKpiData(updatedTreeKpi);
						setTreeKpiIds([]);
					}

					if (idsForCounters.length) {
						const updatedTreeCounters = await handleUpdateTreeWithWorker(treeCounters, idsForCounters);

						setTreeCounters(updatedTreeCounters);
						setInitCountersData(updatedTreeCounters);
						setTreeCountersIds(idsForCounters);
					} else {
						const updatedTreeCounters = await handleUpdateTreeWithWorker(treeCounters, idsForCounters);

						setTreeCounters(updatedTreeCounters);
						setInitCountersData(updatedTreeCounters);
						setTreeCountersIds([]);
					}
				} finally {
					setIsLoadingSetPreset?.(false);
				}
			} else {
				const checkedKpiValues = prepareOptionKpiValues({
					optionsKpi,
					kpiIds: selectedPreset.kpiIds,
				});

				onChangeKpi(checkedKpiValues, false);
			}
		} else {
			resetKpi();
		}

		setIsDirty(false);
	}, [
		dynamicDatesOptions,
		setTreeCounters,
		setTreeKpi,
		setInitKpiData,
		setInitCountersData,
		handleUpdateTreeWithWorker,
		setIsLoadingSetPreset,
		availableCountersIds,
		availableKpiIds,
		setTreeKpiIds,
		setTreeCountersIds,
		treeKpi,
		treeCounters,
		isOranVendor,
		defaultSelectedSitesOnMap,
		minDate,
		onChangeKpi,
		optionsKpi,
		resetField,
		resetKpi,
		resetSitesAndVendor,
		selectedPreset,
		setDefaultDates,
		setIsDirty,
		setValue,
	]);

	const savePreset = useCallback(() => {
		const values = getValues();
		const { sites } = prepareDataForSubmit(values);

		const kpiIds = isOranVendor ? [...values.treeKpiIds, ...values.treeCountersIds] : getKpiIds(values.kpi);
		const dateFromUTC = values.from ? startOfDayInMilliseconds(values.from) : null;
		const dateToUTC = values.to ? endOfDayInMilliseconds(values.to) : null;

		if (!vendor?.value) {
			return;
		}

		const dateSelectionParameters = getDateSelectionParameters({
			isOranVendor,
			values,
			dateFromUTC,
			dateToUTC,
		});

		const presetData: IPresetDTO = {
			vendor: vendor.value,
			aggregationType: values.aggregationType,
			kpiIds: kpiIds,
			sites: sites.length ? sites : null,
			granularity: isOranVendor ? values.granularity?.value : undefined,
			isAutomaticGranularity: isOranVendor ? values.isAutomaticGranularity : undefined,
			...dateSelectionParameters,
		};

		openPresetForm(presetData);
	}, [
		getKpiIds,
		getValues,
		isOranVendor,
		openPresetForm,
		prepareDataForSubmit,
		vendor?.value,
	]);

	const editPreset = useCallback(() => {
		if (!vendor?.value || !presetValue?.id || typeof presetValue.id !== 'string' || !presetValue?.name || !selectedPreset) {
			return;
		}

		const values = getValues();

		const { sites } = prepareDataForSubmit(values);

		const kpiIds = isOranVendor ? [...values.treeKpiIds, ...values.treeCountersIds] : getKpiIds(values.kpi);
		const dateFromUTC = values.from ? startOfDayInMilliseconds(values.from) : null;
		const dateToUTC = values.to ? endOfDayInMilliseconds(values.to) : null;

		const dateSelectionParameters = getDateSelectionParameters({
			isOranVendor,
			values,
			dateFromUTC,
			dateToUTC,
		});

		const presetData: IEditPresetDTO = {
			id: presetValue.id,
			name: presetValue.name,
			vendor: vendor.value,
			aggregationType: values.aggregationType,
			kpiIds: kpiIds,
			sites: sites.length ? sites : null,
			granularity: isOranVendor ? values.granularity?.value : undefined,
			isAutomaticGranularity: isOranVendor ? values.isAutomaticGranularity : undefined,
			isShared: isOranVendor ? presetValue?.isShared : undefined,
			...dateSelectionParameters,
		};

		setIsDirty(false);
		updatePreset({ data: presetData });
	}, [
		presetValue?.isShared,
		getKpiIds,
		getValues,
		isOranVendor,
		prepareDataForSubmit,
		presetValue?.id,
		presetValue?.name,
		selectedPreset,
		updatePreset,
		setIsDirty,
		vendor?.value,
	]);

	return {
		setSelectedPreset,
		savePreset,
		editPreset,
	};
};
